/* eslint-disable max-lines */
import _ from 'underscore'
import moment from 'moment-timezone'
import { Layout, PlotData, ScatterLine, Shape } from 'plotly.js'

import { DeviceMaintenanceTrendGroup, DeviceMetric, DeviceMaintenanceTrend, Trend } from 'types/DeviceMetric'
import { setYaxisRangeBasedOnTracesVisibility } from 'helpers/plotly/yAxisRangeCalculator'

import { color } from 'styles/colors'

import { baseLayout } from 'pages/shared/MaintenanceTrend/chartOptions/baseLayout'

const config: Partial<Plotly.Config> = {
  scrollZoom: false,
  autosizable: true,
  responsive: true,
  showTips: false,
  displaylogo: false,
  displayModeBar: false,
  modeBarButtonsToRemove: ['toImage']
}

const POSITION = [0, 0.91, 0.99] // based on the sort order items position will be mapped range can only be 0 - 1

const getResponsiveYPosition = (screenSize: string | null): number => {
  switch (screenSize) {
    case 'small':
      return 0.40 // Move higher up on small screens
    case 'medium':
      return 0.55 // Slightly adjust for medium screens
    case 'large':
    case 'xl':
      return 0.55 // Default position for large screens
    default:
      return 0.55 // Default fallback
  }
}

const getResponsiveXOffset = (screenSize: string | null, screenWidth: number): { left: number, right: number } => {
  const maxRightOffset = 0.07
  const minScreenWidth = 800
  const minRightOffset = 0.01
  const maxScreenWidth = 4000

  // normalize screenWidth to [0,1] and use as time constant t for exponential decay formula
  const t = Math.max(0, Math.min(1, (screenWidth - minScreenWidth) / (maxScreenWidth - minScreenWidth)))
  // constant k factor - increase to make it approach minRightOffset faster
  const k = 3
  const rightOffset = minRightOffset + (maxRightOffset - minRightOffset) * Math.exp(-k * t)

  switch (screenSize) {
    case 'small':
    case 'medium':
      return { left: -0.05, right: rightOffset } // Closer to axis on small screens
    case 'large':
    case 'xl':
      return { left: -0.03, right: rightOffset } // Default spacing for large screens
    default:
      return { left: -0.03, right: rightOffset } // Default fallback
  }
}

const setCustomXaxisTicksAndRange = (layout: Partial<Plotly.Layout>, timeZone: string, screenSize: string | null) => {
  const startTime = moment.tz(timeZone).subtract(30, 'days')
  const endTime = startTime.clone().add(30, 'days')
  const range = [startTime.format('YYYY-MM-DD'), endTime.format('YYYY-MM-DD')]

  layout.xaxis = {
    ...layout.xaxis,
    range,
    tick0: startTime.format('YYYY-MM-DD'),
    dtick: 86400000
  }

  if (screenSize === 'small' || screenSize === 'medium') {
    layout.xaxis = {
      ...layout.xaxis,
      tickvals: _.range(startTime.valueOf(), endTime.valueOf(), 86400000 * 2).map((val) => moment(val).format('YYYY-MM-DD')).concat([endTime.format('YYYY-MM-DD')])
    }
  }
}

const setVerticalPlotBands = (layout: Partial<Plotly.Layout>, y0: number) => {
  const xaxis = layout.xaxis
  if (xaxis && xaxis.range && xaxis.dtick) {
    const startDate = moment(xaxis.range[0], 'YYYY-MM-DD')
    const endDate = moment(xaxis.range[1], 'YYYY-MM-DD')
    const clonedCurrentDate = startDate.clone()
    const nextDate = clonedCurrentDate.add(xaxis.dtick, 'milliseconds')
    const diffInMinutes = nextDate.diff(startDate, 'minutes')
    let currentDate = nextDate

    const shapes: Array<Partial<Plotly.Shape>> = []

    while (currentDate <= endDate) {
      const x1 = currentDate.clone().subtract(diffInMinutes / 2, 'minutes')
      const x2 = currentDate === endDate ? endDate : currentDate.clone().add(diffInMinutes / 2, 'minutes')

      const shape: Partial<Plotly.Shape> = {
        type: 'rect',
        layer: 'below',
        xref: 'x',
        yref: 'paper',
        x0: x1.format('YYYY-MM-DDTHH:mm:ss.SSS'),
        y0,
        x1: x2.format('YYYY-MM-DDTHH:mm:ss.SSS'),
        y1: 1,
        fillcolor: color('maintenancePlotBandBackground'),
        line: {
          width: 0
        }
      }

      shapes.push(shape)
      currentDate = currentDate.add(diffInMinutes * 2, 'minutes') // add aleternatively
    }

    layout.shapes = [...(layout.shapes || []), ...shapes]
  }
}

export const buildTraceAndConfigChart = (metricAndGroupListData: Array<DeviceMaintenanceTrendGroup | DeviceMetric >, hasAlerts: boolean, trend: DeviceMaintenanceTrend | Trend, screenSize?: string | null, screenWidth?: number): { traces: Array<Partial<PlotData>>, yAxisConfig: Partial<Layout> } => {
  const traces: Array<Partial<PlotData>> = []
  const yAxisConfig: Partial<Layout> = {}
  const trendShapes: Array<Partial<Shape>> = []
  const drawLines = trend.drawLines
  const plotZeroValues = trend.plotZero
  if (trend.hideChart) {
    return { traces, yAxisConfig }
  }

  const [listWithoutTooltipOnlu, listWithTooltipOnly] = metricAndGroupListData.reduce(
    ([list, inTooltip]: [
      Array<DeviceMaintenanceTrendGroup | DeviceMetric>,
      Array<DeviceMaintenanceTrendGroup | DeviceMetric>
    ], item) => {
      if (item && item.showInTooltip) {
        inTooltip.push(item)
      } else {
        list.push(item)
      }
      return [list, inTooltip]
    },
    [[], []]
  )

  listWithTooltipOnly.forEach((item) => {
    const isSingleMetric = 'metricId' in item

    const yAxiskey = (trend.showAlert || hasAlerts) ? `y${Object.keys(yAxisConfig).length + 2}` : `y${Object.keys(yAxisConfig).length + 1}`

    if (isSingleMetric) {
      const { x, y } = createPlotData(item.values, plotZeroValues)
      traces.push(createPlotlyTrace(x, y, yAxiskey, { color: item.color }, item.name, drawLines, item.showInTooltip || false))
    } else {
      _.each(item.metrics.reverse(), (individualMetric) => {
        const { x, y } = createPlotData(individualMetric.values, plotZeroValues)
        traces.push(createPlotlyTrace(x, y, yAxiskey, { color: individualMetric.color }, individualMetric.name, drawLines, item.showInTooltip || false))
      })
    }
  })

  const groupByUnit = _.groupBy(_.sortBy(listWithoutTooltipOnlu, ['sortPosition']), 'unit')
  _.each(_.keys(groupByUnit), (list, index) => {
    const yAxisKey = (trend.showAlert || hasAlerts) ? `y${index + 2}` : `y${index + 1}`
    const basePosition = POSITION[index]
    const positionOffset = 0.005 // Offset for each metric to separate them visually
    const yPositionForAlerts = (trend.showAlert || hasAlerts) ? 0.13 : 0.01
    const xPositionForAlerts = (trend.showAlert || hasAlerts) ? 0.12 : 0

    _.each(groupByUnit[list], (item, subIndex) => {
      const addShapesToTrend = (color, xPosition, x1Position) => {
        trendShapes.push(createYAxisMarker(color, xPosition, x1Position, yPositionForAlerts, yPositionForAlerts))
        trendShapes.push(createYAxisMarker(color, xPosition, x1Position, 0.99, 0.99))
      }

      const isSingleMetric = 'metricId' in item

      if (isSingleMetric) {
        const { x, y } = createPlotData(item.values, plotZeroValues)
        traces.push(createPlotlyTrace(x, y, yAxisKey, { color: item.color }, item.name, drawLines, false))

        const x1Position = basePosition === 0 ? basePosition + 0.01 : basePosition - 0.01

        if (groupByUnit[list].length === 1 && !trend.hideRanges) {
          addShapesToTrend(item.color, basePosition, x1Position)
        }

        const position = basePosition === 0
          ? basePosition + (subIndex * positionOffset)
          : basePosition - (subIndex * positionOffset)

        if (subIndex > 0) {
          trendShapes.push(createYAxisMarker(trend.hideRanges ? 'transparent' : item.color, position, position, xPositionForAlerts, 1))

          if (subIndex === groupByUnit[list].length - 1 && !trend.hideRanges) {
            const xPosition = basePosition === 0 ? position + 0.01 : position

            const x1Position = position === 0 ? xPosition + 0.01 : xPosition - 0.01
            addShapesToTrend(item.color, xPosition, x1Position)
          }
        }
      } else {
        _.each(item.metrics.reverse(), (individualMetric, individualMetricIndex) => {
          const position = basePosition === 0
            ? basePosition + (individualMetricIndex * positionOffset)
            : basePosition - (individualMetricIndex * positionOffset)

          const xPosition = basePosition === 0 ? position + 0.01 : position

          if (individualMetricIndex === item.metrics.length - 1) {
            const x1Position = position === 0 ? xPosition + 0.01 : xPosition - 0.01
            addShapesToTrend(individualMetric.color, xPosition, x1Position)
          }

          const { x, y } = createPlotData(individualMetric.values, plotZeroValues)

          if (individualMetricIndex !== 0) {
            trendShapes.push(createYAxisMarker(individualMetric.color, position, position, xPositionForAlerts, 1))
          }

          traces.push(createPlotlyTrace(x, y, yAxisKey, { color: individualMetric.color }, individualMetric.name, drawLines, false))
        })
      }
    })
    const firstItemOfUnit = groupByUnit[list][0]
    const side = index === 0 ? 'left' : 'right'
    const isFirstItemMetric = 'metricId' in firstItemOfUnit
    const color = trend.hideRanges ? 'transparent' : isFirstItemMetric ? firstItemOfUnit.color : firstItemOfUnit.metrics[0].color
    const yAxis = (trend.showAlert || hasAlerts) ? `yaxis${index + 2}` : `yaxis${index + 1}`
    const domain = (trend.showAlert || hasAlerts) ? [0.12, 1] : undefined
    const overlaying = (trend.showAlert || hasAlerts) ? 'y2' : 'y'
    yAxisConfig[yAxis] = configureYAxis(trend.hideRanges, color, side, POSITION[index], overlaying, domain)

    const xOffset = getResponsiveXOffset(screenSize || null, screenWidth || 0)
    yAxisConfig.annotations = [...yAxisConfig.annotations || [], {
      xref: 'paper',
      yref: 'paper',
      x: side === 'left' ? POSITION[index] + xOffset.left : POSITION[index] + xOffset.right,
      y: getResponsiveYPosition(screenSize || null),
      xanchor: side,
      text: !trend.hideRanges ? firstItemOfUnit.unit : '',
      textangle: side === 'right' ? '90' : '-90',
      showarrow: false,
      font: {
        size: 14
      }
    }]
  })

  // Assign the shapes to yAxisConfig outside the loop
  yAxisConfig.shapes = trendShapes

  return { traces, yAxisConfig }
}

const createYAxisMarker = (color: string, xPosition: number, x1Position: number, yPosition: number, y1Position: number):Partial<Shape> => {
  return {
    type: 'line',
    x0: xPosition,
    y0: yPosition,
    x1: x1Position,
    y1: y1Position,
    xref: 'paper',
    yref: 'paper',
    line: {
      color,
      width: 5
    }
  }
}

const configureYAxis = (hideRanges, color, side, position, overlaying, domain) => {
  return {
    fixedrange: true,
    showgrid: false,
    zeroline: false,
    showline: true,
    showticklabels: !hideRanges,
    linecolor: color,
    linewidth: 5,
    ticklen: 2,
    domain,
    tickcolor: 'transparent',
    overlaying: side === 'right' ? overlaying : '',
    anchor: side === 'right' ? 'free' : '',
    side,
    position,
    rangemode: 'tozero'
  }
}
const createPlotData = (values, plotZeroValues) => {
  const x: Array<string> = []
  const y: Array<string|null> = []
  _.each(values, (metricValue) => {
    if (plotZeroValues) {
      x.push(moment(metricValue.date, 'YYYY-MM-DD').format('YYYY-MM-DD'))
      y.push(metricValue.value)
    } else if (!plotZeroValues && metricValue.value !== '0.0') {
      x.push(moment(metricValue.date, 'YYYY-MM-DD').format('YYYY-MM-DD'))
      y.push(metricValue.value)
    }
  })

  return { x, y }
}

const createPlotlyTrace = (x: Array<string>, y: Array<string| null>, yaxis: string, line: Partial<ScatterLine>, name: string, drawLines: boolean, visible: boolean) : Partial<Plotly.PlotData> => {
  return {
    name,
    opacity: visible ? 0 : 1,
    cliponaxis: false,
    type: 'scatter',
    mode: drawLines ? 'lines+markers' : 'markers',
    line,
    marker: {},
    hoverinfo: 'none',
    x,
    y,
    yaxis,
    xaxis: 'x',
    connectgaps: false
  }
}

const getXAxisDomain = (traces: Array<Partial<PlotData>>): [number, number] => {
  let min = 0.02
  let max = 0.89

  const filterTracesWithOpacity = traces.filter(trace => trace.opacity === 1)
  if (filterTracesWithOpacity.length === 0) {
    return [min, max]
  }
  // group traces by yaxis
  const groupedTraces = _.groupBy(filterTracesWithOpacity, (trace) => trace.yaxis || 'y')
  if (Object.keys(groupedTraces).length > 0) {
    const y2Count = groupedTraces['y2'] ? groupedTraces['y2'].length : 0
    const y3Count = groupedTraces['y3'] ? groupedTraces['y3'].length : 0
    // Each item in the count has a width of 5, so adjust min accordingly
    min = y2Count <= 3 ? min : (min + (y2Count * 5 / 1500))
    max = y3Count <= 3 ? max : (max - (y3Count * 5 / 1500))
  }

  return [min, max]
}
const build = (traces: Array<Partial<Plotly.PlotData>>, yAxisLayout: Partial<Plotly.Layout>, timeZone: string, screenSize: string | null, hasAlerts: boolean, trend: DeviceMaintenanceTrend | Trend, hasData: boolean)
: { layout: Partial<Plotly.Layout>, config: Partial<Plotly.Config> } => {
  const layout: Partial<Plotly.Layout> = JSON.parse(JSON.stringify({
    ...baseLayout,
    ...yAxisLayout,
    annotations: [
      ...(baseLayout.annotations || []),
      ...(yAxisLayout.annotations || [])
    ]
  }))

  const domain = getXAxisDomain(traces)
  layout.xaxis = {
    ...layout.xaxis,
    domain
  }

  setCustomXaxisTicksAndRange(layout, timeZone, screenSize)

  const hasAllZeroValues = traces.every(
    (trace) => Array.isArray(trace.y) && trace.y.every((value) => value === '0.0' || value === null)
  )

  if (trend.hideChart && (trend.showAlert || hasAlerts)) {
    const alertAnnotation = layout.annotations?.find((annotation) => annotation.text === 'alerts')
    if (alertAnnotation) {
      alertAnnotation.visible = true
      alertAnnotation.xshift = 10
      alertAnnotation.yshift = 15
    }
  } else if ((trend.showAlert || hasAlerts) && layout.annotations) {
    layout.annotations.forEach((annotation) => {
      annotation.visible = true
    })

    setYaxisRangeBasedOnTracesVisibility(traces, layout.yaxis2, 'y2', true)
    setYaxisRangeBasedOnTracesVisibility(traces, layout.yaxis3, 'y3', true)
    setYaxisRangeBasedOnTracesVisibility(traces, layout.yaxis4, 'y4', true)
  } else {
    delete layout.yaxis
    setYaxisRangeBasedOnTracesVisibility(traces, layout.yaxis, 'y', true)
    setYaxisRangeBasedOnTracesVisibility(traces, layout.yaxis2, 'y2', true)
    setYaxisRangeBasedOnTracesVisibility(traces, layout.yaxis3, 'y3', true)
  }

  if (trend.showAlert && !hasAlerts && layout.annotations) {
    layout.annotations?.push(
      {
        xref: 'paper',
        yref: 'paper',
        xanchor: 'center',
        yanchor: 'middle',
        text: 'No Alerts',
        align: 'center',
        y: 0.09,
        yshift: trend.hideChart ? 12 : -12,
        xshift: -80,
        opacity: 0.5,
        visible: true,
        showarrow: false,
        captureevents: false,
        font: {
          size: 20
        }
      }
    )
  }

  if ((!hasData || (hasAllZeroValues && trend.showZeroWaterMark)) && !trend.hideChart && trend.waterMark && layout.annotations) {
    layout.annotations?.push({
      xref: 'paper',
      yref: 'paper',
      xanchor: 'center',
      yanchor: 'middle',
      y: 0.5,
      xshift: -80,
      yshift: (trend.showAlert || hasAlerts) ? 15 : 0,
      text: trend.waterMark,
      align: 'center',
      opacity: 0.5,
      visible: true,
      showarrow: false,
      captureevents: false,
      font: {
        size: 28
      }
    })
  }

  const y0 = (trend.showAlert || hasAlerts) ? 0.12 : 0
  if (trend.hideChart) {
    layout.height = 90
    layout.yaxis && (layout.yaxis.domain = [0, 0])
  } else {
    layout.height = 350
    setVerticalPlotBands(layout, y0)
  }

  return { layout, config }
}

export const chartOptionsBuilder = {
  build
}
